import { HttpParams } from '@angular/common/http';
import { Member } from '@b3networks/api/auth';
import { NUMBER_PRODUCT_ID } from '@b3networks/shared/common';
import { format, isAfter, parse } from 'date-fns';
import subDays from 'date-fns/subDays';

export type SubscriptionEmbeded = 'numbers' | 'assignees' | 'prices' | 'features';

export class FindSubscriptionReq {
  sku?: string;
  embed?: SubscriptionEmbeded[];
  productIds?: string[];
  assignee?: string;
  includeAssignees?: boolean;
  statuses?: string;
  uuid?: string;
  skuCodes?: string;
  subscriptionRef?: string;
  number?: string;

  constructor(obj?: Partial<FindSubscriptionReq>) {
    if (obj) {
      Object.assign(this, obj);
    }
  }

  toParams(): HttpParams {
    let params = new HttpParams();
    if (this.statuses) {
      params = params.set('statuses', this.statuses);
    }
    if (this.includeAssignees) {
      params = params.set('includeAssignees', String(this.includeAssignees));
    }
    if (this.sku) {
      params = params.set('sku', this.sku);
    }
    if (this.productIds && this.productIds.length) {
      params = params.set('productIds', this.productIds.join(','));
    }
    if (this.embed && this.embed.length) {
      params = params.set('embed', this.embed.join(','));
    }
    if (this.assignee) {
      params = params.set('assignee', this.assignee);
    }
    if (this.uuid) {
      params = params.set('uuid', this.uuid);
    }
    if (this.skuCodes) {
      params = params.set('skuCodes', this.skuCodes);
    }
    if (this.subscriptionRef) {
      params = params.set('subscriptionRef', this.subscriptionRef);
    }
    if (this.number) {
      params = params.set('number', this.number);
    }

    return params;
  }
}

export interface ProductFeature {
  id: number;
  featureCode: string;
  featureLabel: string;
  featureValue: string;
  isShow: boolean;
  type: string;
}

export class SubscriptionItem {
  amount: number;
  cost: number;
  productId: string;
  productName: string;
  productType?: string;
  blocked: boolean;
  quota: number;
  costNextCycle: number;
  sku: string;
  saleModelCode: string;
  amountNextCycle: number;
  primary: boolean;
  skuName: string;
  logo: string;
  quantity: number;
  features: ProductFeature[];
  originalQuota?: number;
  price: number;
  discount: number;
  nextCycle: number;
  thisCycle: number;
  endDate: string;
  startDate: string;
  disount: number;
  number?: string;

  constructor(obj?: Partial<SubscriptionItem>) {
    if (obj) {
      Object.assign(this, obj);
    }
    this.features = this.features || [];
  }
}

export class Subscription {
  multiplier: number;
  uuid: string;
  trial: boolean;
  orgUuid: string;
  expiryDate: string;
  customerDiscount: number;
  domain: string;
  autoRenew: boolean;
  currency: string;
  activatedDate: string;
  items: SubscriptionItem[] = [];
  renewDate: string;
  status: string;
  assignees: string[] = [];
  numbers: SubscriptionItem[] = [];
  updatedDateTime: string;
  totalAmount: number;
  totalAmountNextCycle?: number;
  totalCostNextCycle?: number;
  totalCost?: number;
  contractNumber?: string;
  primaryProductName?: string;
  primarySkuName?: string;
  subscriptionRef: string;

  constructor(obj?: any) {
    Object.assign(this, obj);
    if (this.items?.length) {
      this.items = this.items.map(item => new SubscriptionItem(item));
      this.totalCost = this.items.map(x => x.cost).reduce((a, b) => a + b);

      // TODO trick to assure total cost is always the same as total amount
      this.totalCostNextCycle = this.items.map(x => x.costNextCycle).reduce((a, b) => a + b);
      if (this.totalCostNextCycle === this.items.map(x => x.amountNextCycle).reduce((a, b) => a + b)) {
        this.totalCostNextCycle = this.totalAmountNextCycle;
      }
    }

    this.numbers = this.numbers || this.items?.filter(x => x.productType === 'NUMBER');
  }

  get numberQuantity() {
    if (!this.numbers || this.numbers?.length === 0) {
      return 0;
    } else {
      return this.numbers?.map(x => x.quantity)?.reduce((a, b) => a + b);
    }
  }

  get addons() {
    return this.items.filter(x => x.primary === false && x.productType !== 'NUMBER');
  }

  get expiryDateInInclusive() {
    return subDays(new Date(this.expiryDate), 1);
  }

  get mainSku(): string {
    const mainSku = this.items.find(item => item.primary);
    return mainSku ? mainSku.sku : null;
  }

  get subscriptionName(): string {
    const primarySubscriptionItem = this.items.find(item => item.primary);
    const name = primarySubscriptionItem && 'skuName' in primarySubscriptionItem ? primarySubscriptionItem.skuName : '';
    return name;
  }

  get subscriptionProductName(): string {
    const primarySubscriptionItem = this.items.find(item => item.primary);
    const name =
      primarySubscriptionItem && 'productName' in primarySubscriptionItem ? primarySubscriptionItem.productName : '';
    return name;
  }

  get description() {
    const numbers = this.numbers.map(item => item.number);
    return `${this.subscriptionName} (${numbers.join(', ')})`;
  }

  get primaryItem() {
    return this.items.find(item => item.primary);
  }

  get blocked() {
    return this.items.some(i => i.blocked);
  }

  get countNumbers() {
    return this.items.filter(i => i.productId === NUMBER_PRODUCT_ID).reduce((sum, item) => sum + item.quantity, 0);
  }

  get features() {
    return this.items.filter(i => i.primary === false && i.productId !== NUMBER_PRODUCT_ID);
  }

  get displayTotalPrice() {
    return Math.round((this.totalAmount / this.multiplier) * 1000) / 1000;
  }

  get featureCodes() {
    const codes = [];
    this.items.forEach(i => i.features.forEach(f => codes.push(f.featureCode)));
    return codes;
  }

  get displayExpiryDate() {
    return format(subDays(parse(this.expiryDate, 'yyyy-MM-dd', new Date()), 1), 'yyyy-MM-dd');
  }
}

export class SubsctiptionRequestParams {
  autoRenew?: boolean;
  salemodel?: string;
  multiplier?: number;
  subscriptionRef?: string;

  constructor(obj?: Partial<SubsctiptionRequestParams>) {
    if (obj) {
      Object.assign(this, obj);
    }
  }
}

export interface RemoveMemberReq {
  subscription: Subscription;
  member: Member;
}

export class RecoveryResponseV2 {
  subscriptionUuid: string;
  primaryProductId: string;
  primaryProductName: string;
  primarySku: string;
  primarySkuName: string;
  amount: number;
  isSelected: boolean;
}

export interface RouterState {
  orgUuid: string;
  recoveryUuid: string;
}

export interface ExportSubscriptionReq {
  emails: string[];
  statuses: string;
  productIds: string[];
}

export class PurchaseRequest4 {
  items: PurchaseItem4[] = [];
  autoRenew: boolean;
  multiplier = 1;
  trial: boolean;
  contractNumber: string;
}

export class PurchaseItem4 {
  constructor(
    public productId: string,
    public sku: string,
    public saleModelCode: string,
    public quantity: number,
    public primary?: boolean,
    public isChunk?: boolean,
    public numberSku?: string
  ) {}
}

export interface SellerConfig {
  enableTrial: boolean;
  enableAlign: boolean;
  enableRefund: boolean;
  renewDaysBefore: number;
}

export class EditItem4 {
  invoiceNumber: string;
  items: PurchaseItem4[];

  constructor(invoiceNumber: string, items: PurchaseItem4[]) {
    this.invoiceNumber = invoiceNumber;
    this.items = items;
  }
}

export class ChangeVariantRequest {
  invoiceNumber: string;
  primaryProduct: string;
  primarySku: string;
  saleModelCode: string;
  effectOnRenewal: boolean;

  constructor(invoiceNumber: string, primaryProduct: string, primarySku: string, saleModelCode: string) {
    this.invoiceNumber = invoiceNumber;
    this.primaryProduct = primaryProduct;
    this.primarySku = primarySku;
    this.saleModelCode = saleModelCode;
    this.effectOnRenewal = false;
  }
}

export class AddonModel {
  productId: string;
  code: string;
  name: string;
  originalQuota: number;
  quota: number;
  price: number;
  amount: number;
  amountNextCycle: number;
  discount: number;
  blocked: boolean;

  constructor(productId: string, name: string, code: string, originalQuota: number) {
    this.productId = productId;
    this.name = name;
    this.code = code;
    this.originalQuota = originalQuota;
    this.quota = originalQuota;
  }
}

export class TransactionLog {
  currency: string;
  description: string;
  multiDescriptions: string[];
  totalAmount: number;
  totalPartnerAmount: number;
  remark: string;
  type: string;
  status: string;
  startDate: string;
  endDate: string;
  createdDateTime: string;
  updatedDateTime: string;

  constructor(params: any) {
    this.currency = params.currency;
    this.description = params.description;
    this.totalAmount = params.totalAmount;
    this.totalPartnerAmount = params.totalPartnerAmount;
    this.remark = params.remark;
    this.type = params.type;
    this.status = params.status;
    this.startDate = params.startDate;
    this.endDate = params.endDate;
    this.createdDateTime = params.createdDateTime;
    this.updatedDateTime = params.updatedDateTime;

    if (!!this.description && this.description.includes('\n')) {
      this.multiDescriptions = this.description.split('\n');
      this.multiDescriptions = this.multiDescriptions.slice(0, this.multiDescriptions.length);
    } else {
      this.multiDescriptions = [];

      if (!this.description) {
        const startDate = format(new Date(this.startDate), 'yyyy-MM-dd');
        const endDate = format(new Date(this.endDate), 'yyyy-MM-dd');
        if (isAfter(new Date(endDate), new Date(startDate))) {
          subDays(new Date(endDate), 1);
        }

        this.description = `from ${format(new Date(startDate), 'dd MMM yyyy')} to ${format(
          new Date(endDate),
          'dd MMM yyyy'
        )}`;
      }
    }
  }

  get getUnsubReason(): string {
    if (this.remark) {
      return JSON.parse(this.remark).reason;
    }

    return null;
  }
}

export class SubscriptionSkuResp {
  sku: string;
  subscriptionCount: number;
  productId: string;
  skuName: string;
  status?: 'ACTIVE' | 'EXPIRED' | 'TERMINATED' | 'SUSPENDED';
}

export interface ExportSubscriptionRequestParams {
  skus?: string;
  statuses?: 'ACTIVE' | 'EXPIRED' | 'TERMINATED' | 'SUSPENDED';
}

export interface ExportSubscriptionResp {
  id: string;
  tempKey: string;
}

export interface SubAlert {
  numberOfSubscriptions: number;
  orgUuid: string;
  orgName: string;
  isSuspended: boolean;
  isBulking: boolean;
}

export interface SubAlertEntry {
  activatedDate: string;
  expiryDate: string;
  items: SubAlertEntryItem[];
  renewDate: string;
  status: string;
  uuid: string;
}

export interface SubAlertEntryItem {
  amount: number;
  amountNextCycle: number;
  blocked: boolean;
  cost: number;
  costNextCycle: number;
  primary: boolean;
  productId: string;
  quota: number;
  saleModelCode: string;
  sku: string;
}

export interface SubAlertEntryView {
  select: boolean;
  uuid: string;
  icon: string;
  productId: string;
  productName: string;
  skuName: string;
  renewDate: string;
  price: number;
  currency: string;
  expiryDate: string;
}

export interface GetAlignPriceReq {
  expiryDate: string;
  subscriptionUuids: string[];
}

export interface SubscriptionAmount {
  subscriptionUuid: string;
  totalAmount: number;
  totalPartnerAmount: number;
}

export interface SubscriptionNotiResponse<SubscriptionNoti> {
  nextCursor: string | null;
  entries: SubscriptionNoti[];
}

export interface SubscriptionNoti {
  uuid: string;
  expiryDate: string;
  lastRenewDate?: string;
  productId: string;
  sku: string;
  currency?: string;
  amountNextCycle?: number;
  failedRenewalReason: string;
  //UI
  primaryProductId: string;
  primaryProductName: string;
  primarySkuName: string;
  logo: string;
  extendPrice: number;
  renewalPrice: number;
}

export interface ExtendRequest {
  subscriptionUuid: string;
  invoiceNumber: string;
  newExpiryDate: string;
  orgUuid: string;
}

export interface ExtendResultItem {
  success: number;
  fail: number;
  successUuids: string[];
  failedUuids: string[];
}

export enum PurchaseType {
  BUY_ADDON,
  BUY_NUMBER,
  CHANGE_PRODUCT_VARIANT,
  EXTEND,
  REACTIVATE,
  CONVERT,
  ALIGN,
  RECOVER,
  TERMINATE
}
